import React, { useState, useEffect, useReducer, useContext, useCallback, useRef } from "react";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Grid,
  Divider,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";

import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import EventIcon from "@material-ui/icons/Event";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import WarningIcon from "@material-ui/icons/Warning";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import SettingsIcon from "@material-ui/icons/Settings";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import SubscriptionModal from "../../components/SubscriptionModal";
import TransactionModal from "../../components/TransactionModal";
import FinanceSummaryCard from "../../components/FinanceSummaryCard";
import MobileRecordCard, { MobileRecordCardSkeleton } from "../../components/MobileRecordCard";
import RelatorioPanel from "./RelatorioPanel";
import ContasReceberPanel from "./ContasReceberPanel";
import FinanceiroConfigDialog from "./FinanceiroConfigDialog";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import moment from "moment";
import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";
import { useLocation, Redirect } from "react-router-dom";

// ─────────────────────────────────────────────
// Reducers
// ─────────────────────────────────────────────

const txReducer = (state, action) => {
  if (action.type === "LOAD") {
    const incoming = action.payload;
    const next = [...state];
    incoming.forEach((item) => {
      const idx = next.findIndex((t) => t.id === item.id);
      if (idx !== -1) next[idx] = item;
      else next.push(item);
    });
    return next;
  }
  if (action.type === "UPDATE") {
    const idx = state.findIndex((t) => t.id === action.payload.id);
    if (idx !== -1) {
      const next = [...state];
      next[idx] = action.payload;
      return next;
    }
    return [action.payload, ...state];
  }
  if (action.type === "DELETE") {
    return state.filter((t) => t.id !== action.payload);
  }
  if (action.type === "RESET") return [];
  return state;
};

// ─────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  summaryGrid: {
    marginBottom: theme.spacing(3),
  },
  urgentRow: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(198,40,40,0.12)"
        : "rgba(255,235,235,0.8)",
  },
  payButton: {
    fontWeight: 700,
    fontSize: "0.75rem",
    borderRadius: 6,
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  filterField: {
    minWidth: 130,
  },
  addButton: {
    marginLeft: "auto",
  },
  categoryDot: {
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: 6,
    verticalAlign: "middle",
  },
  incomeValue: { color: "#2e7d32", fontWeight: 700 },
  expenseValue: { color: "#c62828", fontWeight: 700 },
}));

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const statusConfig = {
  paid:    { label: "Pago",      style: { backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 } },
  overdue: { label: "Vencido",   style: { backgroundColor: "#ffebee", color: "#c62828", fontWeight: 600 } },
  open:    { label: "Em Aberto", style: { backgroundColor: "#fff8e1", color: "#f57f17", fontWeight: 600 } },
};

const getInvoiceStatus = (invoice) => {
  if (invoice.status === "paid") return "paid";
  const days = moment(invoice.dueDate).diff(moment().startOf("day"), "days");
  return days < 0 ? "overdue" : "open";
};

const txStatusConfig = {
  pending:   { label: "Pendente",  style: { backgroundColor: "#fff8e1", color: "#f57f17", fontWeight: 600 } },
  overdue:   { label: "Vencido",   style: { backgroundColor: "#ffebee", color: "#c62828", fontWeight: 600 } },
  paid:      { label: "Pago",      style: { backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 } },
  cancelled: { label: "Cancelado", style: { backgroundColor: "#f5f5f5", color: "#9e9e9e", fontWeight: 600 } },
};

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" });

const getEffectiveTxStatus = (tx) => {
  if (tx.status !== "pending") return tx.status;
  const days = moment(tx.dueDate).diff(moment().startOf("day"), "days");
  return days < 0 ? "overdue" : "pending";
};

// ─────────────────────────────────────────────
// Panel: Faturas (existing content)
// ─────────────────────────────────────────────

const InvoicePanel = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);
  const { finding } = useCompanies();

  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [companyDueDate, setCompanyDueDate] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!user?.companyId) return;
      try {
        const company = await finding(user.companyId);
        setCompanyDueDate(company.dueDate ? moment(company.dueDate) : null);
      } catch (err) {}
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  // O endpoint /invoices/all retorna todas as faturas da empresa de uma vez.
  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices/all");
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handlePay = (invoice) => {
    setSelectedInvoice(invoice);
    setModalOpen(true);
  };

  const pendingInvoices = invoices.filter((inv) => getInvoiceStatus(inv) !== "paid");
  const overdueInvoices = invoices.filter((inv) => getInvoiceStatus(inv) === "overdue");
  const paidInvoices    = invoices.filter((inv) => getInvoiceStatus(inv) === "paid");

  const nextDue = companyDueDate;
  const daysUntilDue = nextDue ? nextDue.diff(moment().startOf("day"), "days") : null;
  const dueTone =
    daysUntilDue === null ? "grey"
    : daysUntilDue < 0   ? "red"
    : daysUntilDue <= 7  ? "amber"
    : "green";
  const dueCaption =
    daysUntilDue === null
      ? undefined
      : daysUntilDue < 0
      ? `Vencida há ${Math.abs(daysUntilDue)} dias`
      : daysUntilDue === 0
      ? "Vence hoje"
      : `Vence em ${daysUntilDue} dias`;

  return (
    <Paper className={classes.mainPaper} variant="outlined">
      <SubscriptionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        Invoice={selectedInvoice}
        onPaid={loadInvoices}
      />

      <Grid container spacing={2} className={classes.summaryGrid}>
        <Grid item xs={12} sm={4}>
          <FinanceSummaryCard
            label="Licença válida até"
            value={nextDue ? nextDue.format("DD/MM/YYYY") : "—"}
            caption={dueCaption}
            icon={<EventIcon />}
            tone={dueTone}
            loading={loading && !nextDue}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FinanceSummaryCard
            label="Faturas pendentes"
            value={
              overdueInvoices.length > 0
                ? `${pendingInvoices.length} (${overdueInvoices.length} vencida${overdueInvoices.length > 1 ? "s" : ""})`
                : `${pendingInvoices.length}`
            }
            caption={pendingInvoices.length === 0 ? "Nenhuma pendência" : "Regularize para evitar bloqueio"}
            icon={<WarningIcon />}
            tone={overdueInvoices.length > 0 ? "red" : "grey"}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FinanceSummaryCard
            label="Total pago (histórico)"
            value={fmtBRL(paidInvoices.reduce((sum, inv) => sum + (inv.value || 0), 0))}
            caption={`${paidInvoices.length} fatura${paidInvoices.length !== 1 ? "s" : ""} paga${paidInvoices.length !== 1 ? "s" : ""}`}
            icon={<CheckCircleOutlineIcon />}
            tone="green"
            loading={loading}
          />
        </Grid>
      </Grid>

      <Divider style={{ marginBottom: 16 }} />

      {isMobile ? (
        <div>
          {loading && invoices.length === 0 &&
            [0, 1, 2].map((i) => <MobileRecordCardSkeleton key={i} />)}

          {!loading && invoices.length === 0 && (
            <Typography align="center" style={{ padding: 32, color: "#9e9e9e" }}>
              Nenhuma fatura encontrada.
            </Typography>
          )}

          {invoices.map((invoice) => {
            const status = getInvoiceStatus(invoice);
            const cfg = statusConfig[status];
            return (
              <MobileRecordCard
                key={invoice.id}
                title={invoice.detail || "Fatura"}
                highlight={status === "overdue"}
                rows={[
                  { label: "Valor", value: <strong>{fmtBRL(invoice.value)}</strong> },
                  { label: "Vencimento", value: moment(invoice.dueDate).format("DD/MM/YYYY") },
                ]}
                chips={
                  <Chip
                    label={cfg.label}
                    size="small"
                    style={{ ...cfg.style, borderRadius: 8, height: 22, fontSize: "0.72rem" }}
                  />
                }
                footer={
                  status !== "paid" ? (
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      color="primary"
                      disableElevation
                      className={classes.payButton}
                      onClick={() => handlePay(invoice)}
                    >
                      Pagar
                    </Button>
                  ) : (
                    <Typography variant="caption" style={{ color: "#2e7d32", fontWeight: 600 }}>
                      ✓ Pago
                    </Typography>
                  )
                }
              />
            );
          })}
        </div>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Descrição</TableCell>
              <TableCell align="center">Valor</TableCell>
              <TableCell align="center">Vencimento</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Ação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((invoice) => {
              const status = getInvoiceStatus(invoice);
              const cfg = statusConfig[status];
              return (
                <TableRow key={invoice.id} className={status === "overdue" ? classes.urgentRow : ""}>
                  <TableCell>{invoice.detail || "—"}</TableCell>
                  <TableCell align="center" style={{ fontWeight: 700 }}>
                    {fmtBRL(invoice.value)}
                  </TableCell>
                  <TableCell align="center">{moment(invoice.dueDate).format("DD/MM/YYYY")}</TableCell>
                  <TableCell align="center">
                    <Chip label={cfg.label} size="small" style={{ ...cfg.style, borderRadius: 8, height: 22, fontSize: "0.72rem" }} />
                  </TableCell>
                  <TableCell align="center">
                    {status !== "paid" ? (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disableElevation
                        className={classes.payButton}
                        onClick={() => handlePay(invoice)}
                      >
                        Pagar
                      </Button>
                    ) : (
                      <Typography variant="caption" style={{ color: "#2e7d32", fontWeight: 600 }}>
                        ✓ Pago
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {loading && <TableRowSkeleton columns={5} />}
            {!loading && invoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" style={{ padding: 32, color: "#9e9e9e" }}>
                  Nenhuma fatura encontrada.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Panel: Caixa (new financial control)
// ─────────────────────────────────────────────

const defaultDateFrom = () => moment().startOf("month").format("YYYY-MM-DD");
const defaultDateTo   = () => moment().endOf("month").format("YYYY-MM-DD");

const CaixaPanel = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [transactions, dispatch] = useReducer(txReducer, []);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo]     = useState(defaultDateTo);
  const [filterType, setFilterType]     = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [dashboard, setDashboard] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [modalOpen, setModalOpen]       = useState(false);
  const [selectedTx, setSelectedTx]     = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const isValidRange = !dateFrom || !dateTo || dateFrom <= dateTo;

  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const { data } = await api.get("/transactions/dashboard", {
        params: { dateFrom, dateTo },
      });
      setDashboard(data);
    } catch (err) {
      toastError(err);
    } finally {
      setDashLoading(false);
    }
  }, [dateFrom, dateTo]);

  const loadTransactions = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const { data } = await api.get("/transactions", {
        params: {
          dateFrom,
          dateTo,
          type: filterType || undefined,
          status: filterStatus || undefined,
          pageNumber: page,
        },
      });
      // Paginação real: cada página substitui a lista.
      dispatch({ type: "RESET" });
      dispatch({ type: "LOAD", payload: data.transactions });
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filterType, filterStatus]);

  // Filtro mudou: volta para a primeira página.
  useEffect(() => {
    setPageNumber(1);
  }, [dateFrom, dateTo, filterType, filterStatus]);

  // Carrega a lista sempre que a página ou os filtros mudarem
  // (loadTransactions muda de identidade quando os filtros mudam).
  useEffect(() => {
    if (!isValidRange) {
      toast.warn("Período inválido: a data inicial é maior que a final.", {
        toastId: "invalid-range",
      });
      return;
    }
    loadTransactions(pageNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTransactions, pageNumber]);

  useEffect(() => {
    if (!isValidRange) return;
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDashboard]);

  const { user } = useContext(AuthContext);

  // Refs para o socket sempre usar os filtros/página atuais sem reconectar.
  const reloadRef = useRef(() => {});
  useEffect(() => {
    reloadRef.current = () => {
      loadDashboard();
      loadTransactions(pageNumber);
    };
  }, [loadDashboard, loadTransactions, pageNumber]);

  useEffect(() => {
    const socket = socketConnection({ companyId: user.companyId });

    // Recarrega a página atual respeitando os filtros ativos, em vez de
    // inserir o item cru na lista (que podia violar filtro/período).
    socket.on("transaction", () => {
      reloadRef.current();
    });

    return () => socket.disconnect();
  }, [user.companyId]);

  const handleSave = () => {
    reloadRef.current();
  };

  const handleDelete = async () => {
    const tx = deleteConfirm;
    setDeleteConfirm(null);
    try {
      await api.delete(`/transactions/${tx.id}`);
      reloadRef.current();
      toast.success("Lançamento excluído.");
    } catch (err) {
      toastError(err);
    }
  };

  const cards = [
    {
      label: "Receitas pagas",
      value: fmtBRL(dashboard?.totalIncome),
      icon: <TrendingUpIcon />,
      tone: "green",
    },
    {
      label: "Despesas pagas",
      value: fmtBRL(dashboard?.totalExpense),
      icon: <TrendingDownIcon />,
      tone: "red",
    },
    {
      label: "Saldo",
      value: fmtBRL(dashboard?.balance),
      icon: <AccountBalanceWalletIcon />,
      tone: (dashboard?.balance ?? 0) >= 0 ? "blue" : "red",
      colorValue: true,
    },
    {
      label: "A receber",
      value: fmtBRL(dashboard?.pendingIncome),
      icon: <AttachMoneyIcon />,
      tone: "amber",
    },
  ];

  return (
    <Paper className={classes.mainPaper} variant="outlined">
      <TransactionModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedTx(null); }}
        transaction={selectedTx}
        onSave={handleSave}
      />

      {/* Confirmação de exclusão */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir lançamento?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Tem certeza que deseja excluir o lançamento{" "}
            <strong>"{deleteConfirm?.description}"</strong>?
            Ele será cancelado e sairá da lista, mas permanece no histórico
            para fins de auditoria.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
          <Button onClick={handleDelete} variant="contained" style={{ backgroundColor: "#c62828", color: "#fff" }}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dashboard cards */}
      <Grid container spacing={2} className={classes.summaryGrid}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <FinanceSummaryCard
              label={card.label}
              value={card.value}
              icon={card.icon}
              tone={card.tone}
              colorValue={card.colorValue}
              loading={dashLoading}
            />
          </Grid>
        ))}
      </Grid>

      <Divider style={{ marginBottom: 16 }} />

      {/* Filters */}
      <div className={classes.filterRow}>
        <TextField
          label="De"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className={classes.filterField}
        />
        <TextField
          label="Até"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className={classes.filterField}
        />
        <FormControl size="small" className={classes.filterField}>
          <InputLabel>Tipo</InputLabel>
          <Select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="income">Receita</MenuItem>
            <MenuItem value="expense">Despesa</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" className={classes.filterField}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="pending">Pendente</MenuItem>
            <MenuItem value="paid">Pago</MenuItem>
            <MenuItem value="cancelled">Cancelado</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          className={classes.addButton}
          onClick={() => { setSelectedTx(null); setModalOpen(true); }}
        >
          Nova Transação
        </Button>
      </div>

      {/* Lista: cards no mobile, tabela no desktop */}
      {isMobile ? (
        <div>
          {loading && transactions.length === 0 &&
            [0, 1, 2].map((i) => <MobileRecordCardSkeleton key={i} />)}

          {!loading && transactions.length === 0 && (
            <Typography align="center" style={{ padding: 32, color: "#9e9e9e" }}>
              Nenhum lançamento encontrado.
            </Typography>
          )}

          {transactions.map((tx) => {
            const effStatus = getEffectiveTxStatus(tx);
            const stCfg = txStatusConfig[effStatus] || txStatusConfig.pending;
            return (
              <MobileRecordCard
                key={tx.id}
                title={tx.description}
                subtitle={tx.contact?.name}
                highlight={effStatus === "overdue"}
                headerAction={
                  <>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => { setSelectedTx(tx); setModalOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton size="small" onClick={() => setDeleteConfirm(tx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                }
                rows={[
                  {
                    label: "Categoria",
                    value: tx.category ? (
                      <>
                        <span className={classes.categoryDot} style={{ backgroundColor: tx.category.color }} />
                        {tx.category.name}
                      </>
                    ) : (
                      "—"
                    ),
                  },
                  {
                    label: "Valor",
                    value: (
                      <span className={tx.type === "income" ? classes.incomeValue : classes.expenseValue}>
                        {fmtBRL(tx.value)}
                      </span>
                    ),
                  },
                  { label: "Vencimento", value: moment(tx.dueDate).format("DD/MM/YYYY") },
                ]}
                chips={
                  <>
                    <Chip
                      label={tx.type === "income" ? "Receita" : "Despesa"}
                      size="small"
                      style={{
                        borderRadius: 8,
                        height: 22,
                        fontSize: "0.72rem",
                        backgroundColor: tx.type === "income" ? "#e8f5e9" : "#ffebee",
                        color: tx.type === "income" ? "#2e7d32" : "#c62828",
                        fontWeight: 600,
                      }}
                    />
                    <Chip
                      label={stCfg.label}
                      size="small"
                      style={{ ...stCfg.style, borderRadius: 8, height: 22, fontSize: "0.72rem" }}
                    />
                  </>
                }
              />
            );
          })}
        </div>
      ) : (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Descrição</TableCell>
            <TableCell>Categoria</TableCell>
            <TableCell align="center">Tipo</TableCell>
            <TableCell align="right">Valor</TableCell>
            <TableCell align="center">Vencimento</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((tx) => {
            const effStatus = getEffectiveTxStatus(tx);
            const stCfg = txStatusConfig[effStatus] || txStatusConfig.pending;
            return (
              <TableRow key={tx.id} className={effStatus === "overdue" ? classes.urgentRow : ""}>
                <TableCell>
                  <Typography variant="body2">{tx.description}</Typography>
                  {tx.contact && (
                    <Typography variant="caption" color="textSecondary">
                      {tx.contact.name}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {tx.category ? (
                    <>
                      <span className={classes.categoryDot} style={{ backgroundColor: tx.category.color }} />
                      {tx.category.name}
                    </>
                  ) : (
                    <span style={{ color: "#bdbdbd" }}>—</span>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={tx.type === "income" ? "Receita" : "Despesa"}
                    size="small"
                    style={{
                      borderRadius: 8,
                      height: 22,
                      fontSize: "0.72rem",
                      backgroundColor: tx.type === "income" ? "#e8f5e9" : "#ffebee",
                      color: tx.type === "income" ? "#2e7d32" : "#c62828",
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <span className={tx.type === "income" ? classes.incomeValue : classes.expenseValue}>
                    {fmtBRL(tx.value)}
                  </span>
                </TableCell>
                <TableCell align="center">{moment(tx.dueDate).format("DD/MM/YYYY")}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={stCfg.label}
                    size="small"
                    style={{ ...stCfg.style, borderRadius: 8, height: 22, fontSize: "0.72rem" }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => { setSelectedTx(tx); setModalOpen(true); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton size="small" onClick={() => setDeleteConfirm(tx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
          {loading && <TableRowSkeleton columns={7} />}
          {!loading && transactions.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center" style={{ padding: 32, color: "#9e9e9e" }}>
                Nenhum lançamento encontrado.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      )}

      {/* Paginação visual */}
      {(hasMore || pageNumber > 1) && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, padding: "16px 0 4px" }}>
          <Button
            size="small"
            variant="outlined"
            disabled={pageNumber === 1 || loading}
            onClick={() => setPageNumber((p) => p - 1)}
          >
            ‹ Anterior
          </Button>
          <Typography variant="body2" style={{ color: "#757575", minWidth: 60, textAlign: "center" }}>
            Página {pageNumber}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={!hasMore || loading}
            onClick={() => setPageNumber((p) => p + 1)}
          >
            Próxima ›
          </Button>
        </div>
      )}
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────

const Financeiro = () => {
  const { licenseExpired, user } = useContext(AuthContext);
  const location = useLocation();
  // Abre direto em "Faturas" (índice 3) quando: a licença está vencida (o usuário
  // vem para cá para pagar) ou quando a navegação pede explicitamente
  // (?tab=faturas — ex.: botão "Renovar agora" da faixa de aviso).
  const wantsInvoices =
    new URLSearchParams(location.search).get("tab") === "faturas";
  const [tab, setTab] = useState(licenseExpired || wantsInvoices ? 3 : 0);
  const [configOpen, setConfigOpen] = useState(false);

  // O menu já esconde o Financeiro de não-admins, mas a rota precisa do
  // mesmo bloqueio para impedir acesso direto pela URL.
  if (user?.profile && user.profile !== "admin") {
    return <Redirect to="/tickets" />;
  }

  return (
    <MainContainer>
      <FinanceiroConfigDialog open={configOpen} onClose={() => setConfigOpen(false)} />

      <MainHeader>
        <Title>Financeiro</Title>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginLeft: "auto" }}
        >
          <Tab label="Caixa" />
          <Tab label="A Receber" />
          <Tab label="Relatórios" />
          <Tab label="Faturas" />
        </Tabs>
        <Tooltip title="Configurações financeiras">
          <IconButton onClick={() => setConfigOpen(true)} style={{ marginLeft: 8 }}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </MainHeader>

      {tab === 0 && <CaixaPanel />}
      {tab === 1 && <ContasReceberPanel />}
      {tab === 2 && <RelatorioPanel />}
      {tab === 3 && <InvoicePanel />}
    </MainContainer>
  );
};

export default Financeiro;
